import {
    useCallback,
    useRef,
    useImperativeHandle,
    forwardRef,
    Ref,
    useState,
    MutableRefObject,
    useEffect,
} from 'react';
import { type ICellRendererParams, LicenseManager } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react'; // React Data Grid Component
import type {
    CsvExportParams,
    FilterChangedEvent,
    FilterModel,
    ColDef,
    ColDefField,
    // ColGroupDef,
    // GridApi,
    GridReadyEvent,
    SortChangedEvent,
    SizeColumnsToContentStrategy,
    // GridReadyEvent,
    // ModuleRegistry,
    TextFilterModel,
    ICombinedSimpleModel,
    SideBarDef,
    ToolPanelDef,
    DisplayedColumnsChangedEvent,
    ColumnState,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css'; // Mandatory CSS required by the Data Grid
import 'ag-grid-community/styles/ag-theme-quartz.css'; // Optional Theme applied to the Data Grid
import LoadingLayout from './LoadingLayout';
import ClickableEventRow from '~/pages/business/ReportEditPage/ReportGrid/ClickableGridRow';
import { REGISTERED_GRID_COMPONENTS } from '~/pages/business/ReportEditPage/ReportGrid/Grid.constants';
import type { EventsReportObject, PaymentReportObject } from '~/lib/Api.types';
import { formatCurrency } from '~/lib/CurrencyHelpers';
import LoadingBar from './LoadingBar';
import AnimatedNumber from './AnimatedNumber';

LicenseManager.setLicenseKey(
    'Using_this_{AG_Charts_and_AG_Grid}_Enterprise_key_{AG-069953}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Be_Family,_Inc.}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{Breely}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{Breely}_need_to_be_licensed___{Breely}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Charts_and_AG_Grid}_Enterprise_versions_released_before_{22_October_2025}____[v3]_[0102]_MTc2MTA4NzYwMDAwMA==8186310b1dd5c07a3d47a53c1fc75396',
);
export type ColumnData = EventsReportObject | PaymentReportObject;
export type ExtraColumnDefinitionData = {
    setSelectedEvent: (event: ColumnData) => void;
    setSelectedFormSubmission: (event: ColumnData) => void;
};

export type CellRendererParams<TData = ColumnData> = ICellRendererParams<TData>;
export type GridInnerComponent = AgGridReact;
export type ColumnDefinition<TData = any> = ColDef<TData>;
export type ColumnDefinitionField<TData = any> = ColDefField<TData>;
export type ColumnDefinitions<TData = any> = Array<
    ColumnDefinition<TData>
> | null;
export type ColumnTextFilterSimple = TextFilterModel;
export type ColumnTextFilterCombined = ICombinedSimpleModel<TextFilterModel>;

export type OnViewChangedCallback<Data> = (
    params:
        | FilterChangedEvent<Data>
        | SortChangedEvent<Data>
        | DisplayedColumnsChangedEvent<Data>,
) => void;

type GridProps<Data = any> = {
    columns: ColumnDefinitions<Data>;
    rowData?: Array<Data>;
    initialSettings?: GridSettings;
    onViewChanged?: OnViewChangedCallback<Data>;
    isLoading?: boolean;
    isFetchingMore?: boolean;
    showTotalRow?: boolean;
};

// Type for holding all settings of ag-Grid
export type GridSettings = {
    filterModel?: FilterModel | null;
    columnState?: Array<ColumnState> | null;
    // columnGroupState: Array<{
    //     groupId: string;
    //     open: boolean;
    // }> | null;
};

export type GridExposedMethods = {
    exportSettings: () => GridSettings | null;
    importSettings: (settings: Partial<GridSettings>) => void;
    downloadCsvData: (exportParams?: CsvExportParams) => void;
};

const AutosizeStrategy: SizeColumnsToContentStrategy = {
    type: 'fitCellContents',
    /** If true, the header won't be included when calculating the column widths. */
    skipHeader: false,
};

const OtherGridOptions = {
    components: {
        [REGISTERED_GRID_COMPONENTS.CLICKABLE_COMPONENT]: ClickableEventRow,
    },
    sideBar: {
        toolPanels: [
            {
                id: 'columns',
                labelDefault: 'Columns',
                labelKey: 'columns',
                iconKey: 'columns',
                toolPanel: 'agColumnsToolPanel',
                toolPanelParams: {
                    suppressRowGroups: true,
                    suppressValues: true,
                    // suppressPivots: true,
                    suppressPivotMode: true,
                },
            } as ToolPanelDef,
        ], // Enables the column tool panel
        //   defaultToolPanel: 'columns', // "Columns" panel is open by default
    } as SideBarDef,
    rowGroupPanelShow: 'onlyWhenGrouping' as
        | 'always'
        | 'onlyWhenGrouping'
        | 'never',
};


const Grid = <TData = any,>(
    {
        columns,
        rowData,
        initialSettings,
        onViewChanged,
        isLoading,
        isFetchingMore,
        showTotalRow,
    }: GridProps<TData>,
    ref: MutableRefObject<GridExposedMethods>,
) => {
    const gridRef = useRef<AgGridReact<TData>>(null);
    const gridIsReadyRef = useRef(false);
    const [gridInternalsInitialized, setGridInternalsInitialized] =
        useState(false);
    const lastSettingsRef = useRef(initialSettings);
    const lastSettings = lastSettingsRef.current;

    useImperativeHandle(ref, () => ({
        // Method to export all relevant grid settings
        exportSettings: (): GridSettings | null => {
            if (!gridRef?.current) return null;

            return {
                filterModel: gridRef.current.api?.getFilterModel?.() || null,
                columnState: gridRef.current.api?.getColumnState?.() || null,
                // columnGroupState: gridRef.current.api?.getColumnGroupState?.() || null,
            };
        },

        // Method to import (rehydrate) settings into the grid
        importSettings: (settings: Partial<GridSettings>) => {
            if (!gridRef?.current || !settings) return;

            // Set each of the settings
            if (settings.filterModel) {
                const currentFilterModel = gridRef.current.api.getFilterModel();
                //console.log('current filter model', currentFilterModel, 'new=', settings.filterModel);
                gridRef.current.api.setFilterModel(settings.filterModel);
            }

            if (settings.columnState) {
                const ret = gridRef.current.api?.applyColumnState?.({
                    state: settings.columnState,
                    applyOrder: true,
                });
            }

            // if (settings.columnGroupState) {
            //     gridRef.current.api?.setColumnGroupState?.(settings.columnGroupState);
            // }
        },

        downloadCsvData: (exportParams?: CsvExportParams) => {
            gridRef.current!.api?.exportDataAsCsv(exportParams);
        },
    }));

    useEffect(() => {
        if (
            gridInternalsInitialized && // if the grid internals have been initialized
            rowData && // and data is available
            !gridIsReadyRef.current // and the grid hasn't been marked as ready yet
        ) {
            if (initialSettings) {
                console.log('importing initial setting', initialSettings);
                // if initial settings are available
                ref?.current?.importSettings(initialSettings);
            }
            gridIsReadyRef.current = true;
        }
    }, [gridInternalsInitialized, initialSettings, gridIsReadyRef.current, ref, rowData]);

    useEffect(() => {
        if (initialSettings && gridRef?.current?.api) {
            // re-import settings on each re-render
            //  if we don't then the column settings go back to the defaults on re-render
            ref?.current?.importSettings(lastSettings);

            // TODO -- what if I change settings, but don't save?
            //  then this still resets
        }
    });

    const onGridReady = useCallback((_: GridReadyEvent) => {
        setGridInternalsInitialized(true);
    }, []);

    const onFilterChanged = useCallback(
        (params: FilterChangedEvent<TData>) => {
            if (gridIsReadyRef.current) {
                onViewChanged?.(params);
                lastSettingsRef.current = ref?.current?.exportSettings();
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [gridIsReadyRef.current],
    );

    const onSortChanged = useCallback(
        (params: SortChangedEvent<TData>) => {
            if (gridIsReadyRef.current) {
                onViewChanged?.(params);
                lastSettingsRef.current = ref?.current?.exportSettings();
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [gridIsReadyRef.current],
    );

    const onColumnChanged = useCallback(
        (event: DisplayedColumnsChangedEvent<TData>) => {
            const sources = ['uiColumnMoved', 'toolPanelUi'];
            if (gridIsReadyRef.current && sources.indexOf(event.source) > -1) {
                onViewChanged?.(event);
                lastSettingsRef.current = ref?.current?.exportSettings();
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [gridIsReadyRef.current],
    );

    return (
        <div className="ag-theme-quartz h-screen">
            <div className="px-2 my-2">
                <div className="flex flex-row justify-end">
                    <div>Records</div>
                    <AnimatedNumber
                        className="flex pl-2 min-w-14 justify-end"
                        number={rowData?.length ?? 0}
                        step={25}
                        shouldAnimate={isFetchingMore}
                    />
                </div>
                {isFetchingMore && <LoadingBar className="px-2" />}
            </div>
            <AgGridReact
                ref={gridRef}
                rowData={rowData ?? []}
                columnDefs={columns}
                sideBar={OtherGridOptions.sideBar}
                onGridReady={onGridReady}
                onFilterChanged={onFilterChanged}
                onSortChanged={onSortChanged}
                onDisplayedColumnsChanged={onColumnChanged}
                autoSizeStrategy={AutosizeStrategy}
                rowGroupPanelShow={OtherGridOptions.rowGroupPanelShow}
                components={OtherGridOptions.components}
                columnTypes={{
                    currency: {
                        width: 150,
                        valueFormatter: ({ value }) => formatCurrency(value),
                    },
                }}
                grandTotalRow={showTotalRow ? 'bottom' : undefined}
            />
            <LoadingLayout isLoading={isLoading} />
        </div>
    );
};

export default forwardRef(Grid) as <TData = any>(
    props: GridProps<TData> & { ref?: Ref<GridExposedMethods> },
) => JSX.Element;
